import { applyActionInPlace } from './game/apply.ts';
import { createGame } from './game/create.ts';
import { err, ok, type Result } from './result.ts';
import { RULES_VERSION } from './version.ts';
import type { ActionEnvelope, ActionError, GameConfig, GameEvent, GameState } from './types.ts';

/** Everything needed to rebuild a game: rules version, config, seed and the accepted actions. */
export interface GameLog {
  rulesVersion: number;
  config: GameConfig;
  seed: string;
  actions: ActionEnvelope[];
}

export interface ReplayError {
  index: number;
  error: ActionError;
}

/** Rebuilds a game from its log, optionally stopping after `upTo` actions. */
export function replay(
  log: GameLog,
  upTo: number = log.actions.length,
): Result<GameState, ReplayError> {
  if (log.rulesVersion !== RULES_VERSION) {
    return err({
      index: -1,
      error: {
        code: 'invalid',
        message: `log is for rules ${log.rulesVersion}, engine is ${RULES_VERSION}`,
      },
    });
  }
  const state = createGame(log.config, log.seed);
  for (let i = 0; i < Math.min(upTo, log.actions.length); i++) {
    const r = applyActionInPlace(state, log.actions[i] as ActionEnvelope);
    if (!r.ok) return err({ index: i, error: r.error });
  }
  return ok(state);
}

/** JSON with sorted keys, so that equal states always give equal text. */
export function canonicalJson(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) return `[${value.map((v) => canonicalJson(v)).join(',')}]`;
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj)
    .filter((k) => obj[k] !== undefined)
    .sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${canonicalJson(obj[k])}`).join(',')}}`;
}

/** A short fingerprint of a state (two independent 32-bit FNV-1a hashes), for drift checks. */
export function stateDigest(state: GameState): string {
  const text = canonicalJson(state);
  let a = 0x811c9dc5;
  let b = 0x01000193 ^ text.length;
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    a = Math.imul(a ^ c, 0x01000193) >>> 0;
    b = Math.imul(b + c, 0x85ebca6b) >>> 0;
    b ^= b >>> 13;
  }
  return `${a.toString(16).padStart(8, '0')}${b.toString(16).padStart(8, '0')}`;
}

/** Owns a game's state and records every accepted action, so it can be replayed. */
export class Game {
  readonly state: GameState;
  readonly log: GameLog;

  constructor(config: GameConfig, seed: string) {
    this.state = createGame(config, seed);
    this.log = { rulesVersion: RULES_VERSION, config: structuredClone(config), seed, actions: [] };
  }

  /** Applies an action in place; only accepted actions enter the log. */
  act(env: ActionEnvelope): Result<GameEvent[], ActionError> {
    const r = applyActionInPlace(this.state, env);
    if (r.ok) this.log.actions.push(structuredClone(env));
    return r;
  }

  get over(): boolean {
    return this.state.phase === 'ended';
  }
}
